/* ============================================================
   Holiday Prints — section-holiday-prints.js
   Swiper carousel for the print swatch strip. Swiper is loaded on
   demand by snippets/swiper-loader.liquid (window.pfLoadSwiper).
   While every swatch fits, the row is centred and the arrows are
   hidden. Once they overflow, the strip loops endlessly: Swiper's
   loop needs more slides than a screenful plus one arrow jump, so
   the swatches are duplicated (hidden from assistive tech) first.
   ============================================================ */

if (!customElements.get('holiday-prints-track')) {
  class HolidayPrintsTrack extends HTMLElement {
    swiper = null;
    isLooping = false;

    connectedCallback() {
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onResize = this.onResize.bind(this);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      window.pfLoadSwiper(this).then(
        () => this.isConnected && this.initSwiper(),
        () => {},
      );
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.destroySwiper();
    }

    get track() {
      return this.querySelector('.holiday-prints__track');
    }

    get wrapper() {
      return this.querySelector('.holiday-prints__wrapper');
    }

    get originalSlides() {
      return [...this.wrapper.querySelectorAll('.holiday-prints__card:not(.holiday-prints__card--clone)')];
    }

    get gap() {
      return window.matchMedia('(min-width: 750px)').matches ? 16 : 6;
    }

    // Width of one swatch plus its gap
    get slideStep() {
      return (this.originalSlides[0]?.offsetWidth || 116) + this.gap;
    }

    get overflows() {
      const slideCount = this.originalSlides.length;
      const rowWidth = slideCount * this.slideStep - this.gap;
      return rowWidth > this.track.clientWidth;
    }

    initSwiper() {
      if (!window.Swiper || this.swiper || !this.track || this.originalSlides.length === 0) return;

      this.isLooping = this.overflows;
      this.syncClones();

      this.swiper = new window.Swiper(this.track, {
        slidesPerView: 'auto',
        slidesPerGroupAuto: true, // an arrow click moves one screen of swatches
        loop: this.isLooping,
        spaceBetween: 6,
        centerInsufficientSlides: true,
        watchOverflow: true,
        grabCursor: true,
        navigation: {
          prevEl: this.querySelector('.holiday-prints__arrow--prev'),
          nextEl: this.querySelector('.holiday-prints__arrow--next'),
        },
        a11y: {
          prevSlideMessage: this.dataset.prevLabel,
          nextSlideMessage: this.dataset.nextLabel,
          // Slides are links; a "group" role and "1 / 14" label would hide that from screen readers
          slideRole: null,
          slideLabelMessage: '',
        },
        breakpoints: {
          750: {
            spaceBetween: 16,
          },
        },
        on: {
          resize: this.onResize,
        },
      });
    }

    destroySwiper() {
      this.swiper?.destroy(true, true);
      this.swiper = null;
    }

    // Loop needs a screenful plus one arrow jump on either side of the view, so keep
    // at least three screens of swatches in the track. Copies are only added while looping.
    syncClones() {
      for (const clone of this.wrapper.querySelectorAll('.holiday-prints__card--clone')) {
        clone.remove();
      }

      if (!this.isLooping) return;

      const originals = this.originalSlides;
      const slidesPerScreen = Math.ceil(this.track.clientWidth / this.slideStep);
      const copies = Math.max(1, Math.ceil((slidesPerScreen * 3) / originals.length) - 1);

      for (let copy = 0; copy < copies; copy++) {
        for (const slide of originals) {
          this.wrapper.append(this.createClone(slide));
        }
      }
    }

    createClone(slide) {
      const clone = slide.cloneNode(true);
      clone.classList.add('holiday-prints__card--clone');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('tabindex', '-1');

      // Keep the theme editor from treating copies as blocks
      for (const attribute of [...clone.attributes]) {
        if (attribute.name.startsWith('data-shopify')) clone.removeAttribute(attribute.name);
      }

      return clone;
    }

    // Rebuild when a resize moves the strip between "fits" (centred, no loop) and "overflows" (loop)
    onResize() {
      if (this.overflows === this.isLooping) return;

      this.destroySwiper();
      this.initSwiper();
    }

    // Theme editor: bring the selected swatch into view
    onBlockSelect(event) {
      if (!this.swiper) return;

      const index = this.originalSlides.findIndex((slide) => slide.contains(event.target));

      if (index === -1) return;

      if (this.isLooping) {
        this.swiper.slideToLoop(index);
        return;
      }

      this.swiper.slideTo(index);
    }
  }

  customElements.define('holiday-prints-track', HolidayPrintsTrack);
}
